import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { DEFAULT_SCOPES } from './scopes';
import { toast } from "sonner"

interface AuthContextType {
  getToken: () => Promise<string | null>;
  login: () => void;
  signIn: () => void;
  logout: () => void;
  userEmail: string | null;
  userName: string | null;
  isInProgress: boolean;
  silentAuthAttempted: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [silentAuthAttempted, setSilentAuthAttempted] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Log auth state changes
  useEffect(() => {
    console.log('[AUTH] Auth state updated:', {
      isAuthenticated,
      inProgress,
      accountsCount: accounts.length,
      activeAccount: accounts[0]?.username || 'none',
      timestamp: new Date().toISOString()
    });
  }, [isAuthenticated, inProgress, accounts]);

  // Try silent SSO login on initial load for all routes
  useEffect(() => {
    const attemptSilentLogin = async () => {
      // Only attempt if we haven't tried yet and there's no active interaction
      if (!silentAuthAttempted && inProgress === InteractionStatus.None) {
        console.log('[AUTH] Attempting silent SSO login');
        
        try {
          // First, check if there's a hint we can use (e.g., from a previous session)
          const loginHint = instance.getAllAccounts()[0]?.username;
          
          // Use ssoSilent to attempt a background login
          const result = await instance.ssoSilent({
            scopes: DEFAULT_SCOPES,
            loginHint: loginHint
          });
          
          console.log('[AUTH] Silent SSO login successful', {
            account: result.account.username,
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          // Silently fail - don't redirect or show errors
          console.log('[AUTH] Silent SSO login failed, but continuing silently', {
            errorType: error instanceof InteractionRequiredAuthError ? 'InteractionRequired' : 'Other',
            error: error instanceof Error ? error.message : String(error),
            timestamp: new Date().toISOString()
          });
        } finally {
          // Mark that we've attempted silent auth, regardless of outcome
          setSilentAuthAttempted(true);
        }
      }
    };
    
    attemptSilentLogin();
  }, [instance, inProgress, silentAuthAttempted]);

  // Extract user information from token claims
  const extractUserInfo = (tokenClaims: Record<string, unknown>) => {
    
    // Handle email
    if ("email" in tokenClaims) {
      setUserEmail(tokenClaims.email as string);
    }
    
    // Handle name - prioritize given_name + family_name if available
    if ("given_name" in tokenClaims && "family_name" in tokenClaims) {
      const givenName = tokenClaims.given_name as string;
      const familyName = tokenClaims.family_name as string;
      setUserName(`${givenName} ${familyName}`);
    } 
    // Fallback to name field if it's meaningful
    else if ("name" in tokenClaims && tokenClaims.name !== "unknown") {
      setUserName(tokenClaims.name as string);
    } 
  };

  const getToken = async (): Promise<string | null> => {
    if (accounts.length === 0) {
      console.log('[AUTH] getToken: No accounts available');
      return null;
    }
    
    console.log('[AUTH] getToken: Attempting to acquire token silently', {
      account: accounts[0].username,
      scopes: DEFAULT_SCOPES
    });
    
    try {
      const response = await instance.acquireTokenSilent({
        scopes: DEFAULT_SCOPES,
        account: accounts[0],
        state: JSON.stringify({
          redirectUrl: window.location.pathname + window.location.search
        })
      });
      console.log('[AUTH] getToken: Token acquired silently', {
        tokenExpiration: response.expiresOn,
        tokenType: response.tokenType
      });

      // Extract user info from token claims
      const tokenClaims = response.idTokenClaims;
      if (tokenClaims && typeof tokenClaims === 'object') {
        extractUserInfo(tokenClaims as Record<string, unknown>);
      }

      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log('[AUTH] getToken: Interaction required, trying popup');
        try {
          const response = await instance.acquireTokenPopup({
            scopes: DEFAULT_SCOPES,
            account: accounts[0]
          });
          console.log('[AUTH] getToken: Token acquired via popup', {
            tokenExpiration: response.expiresOn,
            tokenType: response.tokenType
          });

          // Extract user info from token claims
          const tokenClaims = response.idTokenClaims;
          if (tokenClaims && typeof tokenClaims === 'object') {
            extractUserInfo(tokenClaims as Record<string, unknown>);
          }

          return response.accessToken;
        } catch (popupError) {
          console.error('[AUTH] getToken: Popup acquisition failed', popupError);
          toast.error('Authentication failed. Please try again.');
        }
      } else {
        console.error('[AUTH] getToken: Silent acquisition failed', error);
      }
      return null;
    }
  };

  const login = () => {
    // Include both pathname and search in the redirect URL
    const currentUrl = window.location.pathname + window.location.search;
    
    const state = JSON.stringify({
      redirectUrl: currentUrl
    });
    
    console.log('[AUTH] login: Initiating login redirect', {
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      fullRedirectUrl: currentUrl,
      redirectState: state,
      timestamp: new Date().toISOString()
    });
    
    instance.loginRedirect({
      scopes: DEFAULT_SCOPES,
      state
    });
  };


  const signIn = () => {
    // Include both pathname and search in the redirect URL
    const currentUrl = window.location.pathname + window.location.search;
    
    const state = JSON.stringify({
      redirectUrl: currentUrl
    });
    
    console.log('[AUTH] login: Initiating login redirect', {
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      fullRedirectUrl: currentUrl,
      redirectState: state,
      timestamp: new Date().toISOString()
    });
    
    instance.loginRedirect({
      scopes: DEFAULT_SCOPES,
      state,
      prompt: "create"
    });
  };


  const logout = () => {
    console.log('[AUTH] logout: Initiating logout', {
      account: accounts[0]?.username || 'unknown',
      timestamp: new Date().toISOString()
    });
    
    instance.logoutRedirect();
  };

  const isInProgress = inProgress !== 'none';

  const value: AuthContextType = {
    getToken,
    login,
    signIn,
    logout,
    userEmail,
    userName,
    isInProgress,
    silentAuthAttempted
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};